import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { styled } from '@mui/material/styles';
import { APIProvider, Map, InfoWindow } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import DeviceStateIndicator from '@/components/device/DeviceStateIndicator.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import StateIndicator from '@/components/global/map/StateIndicator.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';

const MAP_WIDTH = 374;
const MAP_ID = 'ef998239259c4672dac8d1bc';

const StyledNoMapBox = styled(Box)({
    '&.not-available': {
        width: MAP_WIDTH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '16px',
        backgroundColor: Palette.gray.gray100,
        minHeight: '200px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            display: 'none',
        },
    },
});

const StyledMapBox = styled(Box)({
    '&.map-box': {
        width: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            margin: '0',
        },
        '& .gm-style-iw-chr': {
            display: 'none',
        },
        '& .gm-style-iw-d': {
            padding: '0',
            overflow: 'hidden !important',
        },
        '& .gm-style-iw.gm-style-iw-c': {
            padding: '0',
        },
    },
});

const DetailsMap = ({ record, collectionPoint }) => {
    const google_maps_api_key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const translate = useTranslate();

    if (!google_maps_api_key) {
        return (
            <StyledNoMapBox className='not-available' sx={{ minWidth: record === collectionPoint ? '100%' : 'max-content' }}>
                <Typography variant='body2' color='text.secondary'>
                    {translate('common.map.noMapKey')}
                </Typography>
            </StyledNoMapBox>
        );
    }
    if (!collectionPoint || typeof collectionPoint.latitude !== 'number' || typeof collectionPoint.longitude !== 'number') {
        return (
            <StyledNoMapBox className='not-available'>
                <Typography variant='body2' color='text.secondary'>
                    {translate('common.map.noLocationData')}
                </Typography>
            </StyledNoMapBox>
        );
    }

    if (record === collectionPoint) {
        return (
            <StyledMapBox className='map-box' sx={{ width: '400px', margin: '12px 0' }}>
                <APIProvider apiKey={google_maps_api_key} libraries={['marker']}>
                    <Map
                        mapId={MAP_ID}
                        defaultZoom={17}
                        defaultCenter={{ lat: collectionPoint.latitude, lng: collectionPoint.longitude }}
                        gestureHandling={'greedy'}
                        disableDefaultUI
                        style={{ width: '100%', height: '400px' }}
                    >
                        <InfoWindow
                            position={{ lat: collectionPoint.latitude, lng: collectionPoint.longitude }}
                            title={translate('common.map.deviceLocation')}
                            onClick={() => {}}
                        >
                            <StateIndicator>
                                <DeviceStateIndicator
                                    record={record.devices}
                                    stateLabel={`${translate('resources.device.fields.status')}: `}
                                    className='state-inner'
                                    sx={{
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '0',
                                        '& .state-details': {
                                            width: '100%',
                                            justifyContent: 'space-between',
                                        },
                                    }}
                                />
                            </StateIndicator>
                        </InfoWindow>
                    </Map>
                </APIProvider>
            </StyledMapBox>
        );
    }

    return (
        <StyledMapBox className='map-box'>
            <APIProvider apiKey={google_maps_api_key} libraries={['marker']}>
                <Map
                    mapId={MAP_ID}
                    defaultZoom={17}
                    defaultCenter={{ lat: collectionPoint.latitude, lng: collectionPoint.longitude }}
                    gestureHandling={'greedy'}
                    disableDefaultUI
                    style={{ width: '100%', height: '100%' }}
                >
                    <InfoWindow
                        position={{ lat: collectionPoint.latitude, lng: collectionPoint.longitude }}
                        title={translate('common.map.deviceLocation')}
                        onClick={() => {}}
                    >
                        <StateIndicator>
                            <DeviceStateIndicator
                                record={record}
                                stateLabel={`${translate('resources.device.fields.status')}: `}
                                className='state-inner'
                                sx={{
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '0',
                                    '& .state-details': {
                                        width: '100%',
                                        justifyContent: 'space-between',
                                    },
                                }}
                            />
                        </StateIndicator>
                    </InfoWindow>
                </Map>
            </APIProvider>
        </StyledMapBox>
    );
};

DetailsMap.propTypes = {
    record: PropTypes.object,
    collectionPoint: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
    }),
};

export default DetailsMap;
